import React from 'react';
import {
  Alert,
  Box,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type {
  CreativeProductEconomics,
  CreativeProductEconomicsBasisKey,
  CreativeProductEconomicsEntry,
  CreativeProductEconomicsShare,
  CreativeProductEconomicsSignals,
} from '../../creativeDecisions';
import { CREATIVE_DECISIONS_TEST_IDS } from '../../testIds';
import { formatMinorMoney, humanizeCreativeReasonCode } from './creativeDecisionFormat';

const BASIS_LABELS: Record<CreativeProductEconomicsBasisKey, string> = {
  gross_collected: 'gross',
  net_collected: 'net',
  settlement_cash: 'settlement',
  contribution: 'contribution',
};

/**
 * Per-product realized economics (cadence-program C1-I0, realized layer).
 * Money is attributed to the product of its OWN billing cycle (owner
 * decision: re-attribution on plan change); entrants and trial counters keep
 * entry-offer semantics. Gross is AUDIT context only — the decision basis
 * stays in MoneySection. Currencies are never summed: entries render per
 * original-currency group. The projected layer (predictive) arrives in C1-I1.
 */
export function CreativeProductEconomicsSection({
  economics,
}: {
  economics?: CreativeProductEconomics;
}): React.ReactElement | null {
  if (!economics) return null;

  const stateColor = economics.state === 'available'
    ? 'success'
    : economics.state === 'partial' ? 'warning' : 'default';
  const groups = groupByCurrency(economics.products);

  return (
    <Box
      component="section"
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomics}
    >
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 750 }}>Product economics</Typography>
        <Chip
          size="small"
          variant="outlined"
          color={stateColor}
          label={economics.state}
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsState}
        />
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Realized to date per product · money counts under the product of its own billing cycle ·
        gross shown as audit context only · realized figures undercount young cohorts
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Projected values are predictive estimates (sBG with account shrinkage) comparing offers
        inside this deployment context only. The observed product mix is a delivered-policy
        result, not an experiment.
      </Typography>
      {/* W-A G5 (DISC-AC-013): per-product freshness — reconciled-through date
          + vouched-prefix blocking count; a typed absence when no cycle has
          reconciled yet, never a silently missing element. */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 1 }}
        data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsDataThrough}
      >
        {economics.dataThrough
          ? `Reconciled through ${economics.dataThrough}${(() => {
            const blocked = economics.products.reduce(
              (sum, product) => sum + (product.realizedByBasis.gross_collected?.blockedCycleCount ?? 0),
              0,
            );
            return blocked > 0 ? ` · ${blocked} ${blocked === 1 ? 'cycle' : 'cycles'} blocked` : '';
          })()}`
          : 'No reconciled cycle yet — money values wait for the first reconciled day.'}
      </Typography>

      {economics.state === 'unavailable' ? (
        <Alert severity="info">
          {`No product economics exist for this deployment in the selected window (${humanReasons(economics.reasonCodes)}). Nothing is estimated.`}
        </Alert>
      ) : (
        <>
          {economics.reasonCodes.length > 0 && (
            <Alert severity="warning" sx={{ mb: 1.5 }}>
              {`Some cells are partial: ${humanReasons(economics.reasonCodes)}. Blocked money renders as n/a — amounts are never invented.`}
            </Alert>
          )}
          <SignalsStrip signals={economics.signals} />
          {groups.map((group) => (
            <Box
              key={group.key}
              sx={{ mb: 2 }}
              data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsCurrency(group.key)}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, display: 'block', mb: 0.5, letterSpacing: 0.4 }}
                color="text.secondary"
              >
                {group.label}
              </Typography>
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 560 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="right">Entrants</TableCell>
                      <TableCell align="right">Trial → paid</TableCell>
                      <TableCell align="right">Cycles</TableCell>
                      <TableCell align="right">Realized (gross · audit)</TableCell>
                      <TableCell align="right">Expected checks</TableCell>
                      <TableCell align="right">LTV12 / entrant</TableCell>
                      <TableCell>Bases</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.entries.map((entry) => (
                      <ProductRow
                        key={productKey(entry)}
                        entry={entry}
                        rescindHistory={economics.signals?.rescindHistory}
                      />
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </Box>
          ))}
          <RealizedPaybackLine economics={economics} />
        </>
      )}
    </Box>
  );
}

/**
 * C1-I2 watch-only cheap-signal strip. Every share renders WITH its
 * denominator (C1-AC-012); a below-floor denominator shows raw counts and no
 * percentage. Watch-only framing is part of the copy — these chips are
 * evidence for a human, never a verdict input.
 */
function SignalsStrip({
  signals,
}: {
  signals?: CreativeProductEconomicsSignals;
}): React.ReactElement | null {
  if (!signals || signals.state === 'unavailable') return null;
  const planMix = signals.planMix;
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      sx={{ mb: 1.5 }}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsSignals}
    >
      <Tooltip title="Watch-only evidence — day-0 signals for a human; never a verdict or model input.">
        <Chip
          size="small"
          variant="outlined"
          label="watch-only signals"
          sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
        />
      </Tooltip>
      {planMix.state !== 'unavailable' && (
        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          useFlexGap
          flexWrap="wrap"
          data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsPlanMix}
        >
          <Typography variant="caption" color="text.secondary">
            {`plan mix (${planMix.entrantsTotal} entrants):`}
          </Typography>
          {planMix.entries.map((entry) => (
            <Chip
              key={entry.cadenceClass}
              size="small"
              variant="outlined"
              label={entry.share !== undefined
                ? `${entry.cadenceClass.replace('_', '-')} ${(entry.share * 100).toFixed(0)}% (${entry.entrants}/${planMix.entrantsTotal})`
                : `${entry.cadenceClass.replace('_', '-')} ${entry.entrants}`}
              sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
            />
          ))}
          {planMix.state === 'below_floor' && (
            <Typography variant="caption" color="text.secondary">
              {`below floor — no share claim on n = ${planMix.entrantsTotal}`}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
}

/**
 * D6 stage 1 pairing rule: the pending-cancel share NEVER renders without the
 * historical rescind correction next to it («X% pending · historically Y%
 * return») — the pairing lives in this one component by construction.
 */
function CancelIntentChip({
  entry,
  rescindHistory,
}: {
  entry: CreativeProductEconomicsEntry;
  rescindHistory?: CreativeProductEconomicsShare;
}): React.ReactElement | null {
  const intent = entry.cancelIntent;
  if (!intent || intent.pendingCancel.state === 'unavailable') return null;
  const pending = intent.pendingCancel;
  const pendingLabel = pending.state === 'available' && pending.share !== undefined
    ? `${(pending.share * 100).toFixed(0)}% pending (${pending.numerator}/${pending.denominator} active)`
    : `pending ${pending.numerator}/${pending.denominator} active · below floor`;
  const rescindLabel = rescindHistory === undefined || rescindHistory.state === 'unavailable'
    ? 'rescind history n/a'
    : rescindHistory.state === 'available' && rescindHistory.share !== undefined
      ? `historically ${(rescindHistory.share * 100).toFixed(0)}% return`
      : `rescind history sparse (${rescindHistory.numerator}/${rescindHistory.denominator})`;
  return (
    <Tooltip title="Watch-only evidence: current cancel-at-period-end flags among this product's active entrants, paired with the project-wide historical rescind rate. Never a verdict input.">
      <Chip
        size="small"
        variant="outlined"
        color={pending.state === 'available' ? 'warning' : 'default'}
        label={`${pendingLabel} · ${rescindLabel}`}
        sx={{ mt: 0.5, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
        data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsCancelIntent(productKey(entry))}
      />
    </Tooltip>
  );
}

function ProductRow({
  entry,
  rescindHistory,
}: {
  entry: CreativeProductEconomicsEntry;
  rescindHistory?: CreativeProductEconomicsShare;
}): React.ReactElement {
  const gross = entry.realizedByBasis.gross_collected;
  const cycleParts = [
    ...(entry.initialPaymentCycleCount > 0 ? [`${entry.initialPaymentCycleCount} initial`] : []),
    ...(entry.renewalCycleCount > 0 ? [`${entry.renewalCycleCount} renewal`] : []),
    ...(entry.oneTimeCycleCount > 0 ? [`${entry.oneTimeCycleCount} one-time`] : []),
    ...(entry.trialFeeCycleCount > 0 ? [`${entry.trialFeeCycleCount} trial fee`] : []),
  ];
  return (
    <TableRow data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsProduct(productKey(entry))}>
      <TableCell>
        <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
          {productDisplayName(entry)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', fontStyle: entry.identity.priceState === 'provider' ? 'normal' : 'italic' }}
        >
          {productNominalLabel(entry)}
        </Typography>
        {entry.cohortAgeDays !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`cohort ${entry.cohortAgeDays}d old`}
          </Typography>
        )}
        {entry.projected && (
          <Chip
            size="small"
            variant="outlined"
            color={entry.projected.modelClass === 'unavailable' ? 'default'
              : entry.projected.maturityState === 'too_young' ? 'warning' : 'success'}
            label={entry.projected.modelClass === 'unavailable'
              ? `unavailable · ${humanReasons(entry.projected.unavailableReasons) || 'no model'}`
              : `${entry.projected.modelClass}${entry.projected.maturityState === 'too_young' ? ' · too_young' : ''} · predictive`}
            sx={{ mt: 0.5, height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
          />
        )}
        <CancelIntentChip entry={entry} rescindHistory={rescindHistory} />
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption">{entry.entrySubscriptions}</Typography>
        {entry.tierMix && entry.tierMix.state === 'available'
          && entry.tierMix.share !== undefined && entry.tierMix.share < 1 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`${(entry.tierMix.share * 100).toFixed(0)}% of product (${entry.tierMix.numerator}/${entry.tierMix.denominator})`}
          </Typography>
        )}
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption" color={entry.trialStartCount > 0 ? 'text.primary' : 'text.secondary'}>
          {entry.trialStartCount > 0
            ? `${entry.trialConversionCount} / ${entry.trialStartCount}`
            : 'no trial'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        <Typography variant="caption">
          {cycleParts.length > 0 ? cycleParts.join(' · ') : '—'}
        </Typography>
      </TableCell>
      <TableCell align="right">
        {(gross.status === 'available' || gross.status === 'partial')
          && gross.amountMinor !== null
          && entry.originalCurrency && entry.currencyExponent !== undefined ? (
            <>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>
                {`${gross.status === 'partial' ? '≥ ' : ''}${formatMinorMoney(gross.amountMinor, entry.originalCurrency, entry.currencyExponent)}`}
              </Typography>
              {gross.status === 'partial' && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`${gross.blockedCycleCount} cycle${gross.blockedCycleCount === 1 ? '' : 's'} pending`}
                </Typography>
              )}
              {(entry.trialFeeGrossStatus === 'available' || entry.trialFeeGrossStatus === 'partial')
                && entry.trialFeeGrossMinor !== null && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  {`+ ${entry.trialFeeGrossStatus === 'partial' ? '≥ ' : ''}${formatMinorMoney(entry.trialFeeGrossMinor, entry.originalCurrency, entry.currencyExponent)} trial fees`}
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              {`n/a · ${humanReasons(gross.reasonCodes) || gross.status}`}
            </Typography>
          )}
      </TableCell>
      <TableCell align="right">
        <ProjectedChecksCell entry={entry} />
      </TableCell>
      <TableCell align="right">
        <ProjectedLtvCell entry={entry} />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
          {(Object.keys(BASIS_LABELS) as CreativeProductEconomicsBasisKey[]).map((key) => {
            const basis = entry.realizedByBasis[key];
            return (
              <Tooltip
                key={key}
                title={basis.status === 'available'
                  ? `${BASIS_LABELS[key]} basis available`
                  : basis.status === 'partial'
                    ? `${BASIS_LABELS[key]}: lower bound — ${basis.blockedCycleCount} cycles pending`
                    : `${BASIS_LABELS[key]}: ${humanReasons(basis.reasonCodes) || basis.status}`}
              >
                <Chip
                  size="small"
                  variant="outlined"
                  color={basis.status === 'available' ? 'success'
                    : basis.status === 'partial' ? 'warning' : 'default'}
                  label={`${BASIS_LABELS[key]} ${basis.status}`}
                  sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 10 } }}
                />
              </Tooltip>
            );
          })}
        </Stack>
      </TableCell>
    </TableRow>
  );
}

function ProjectedChecksCell({
  entry,
}: {
  entry: CreativeProductEconomicsEntry;
}): React.ReactElement {
  const projected = entry.projected;
  if (!projected || projected.state === 'unavailable') {
    return (
      <Typography variant="caption" color="text.secondary">
        {projected ? `n/a · ${humanReasons(projected.unavailableReasons) || 'unavailable'}` : '—'}
      </Typography>
    );
  }
  if ((projected.modelClass === 'sbg_monthly' || projected.modelClass === 'sbg_weekly')
    && projected.expectedChecksP10 !== undefined
    && projected.expectedChecksP50 !== undefined
    && projected.expectedChecksP90 !== undefined) {
    return (
      <>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {projected.trialDecomposition
            ? `P(convert) ${(projected.trialDecomposition.pConvert * 100).toFixed(0)}% × ${projected.expectedChecksP50}`
            : `${projected.expectedChecksP50}`}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {`P10 ${projected.expectedChecksP10} — P90 ${projected.expectedChecksP90}`}
        </Typography>
        <CacLine projected={projected} />
      </>
    );
  }
  return (
    <>
      <Typography variant="caption">
        {projected.modelClass === 'one_time' ? '1.0 · single payment' : '1.0 · upfront'}
      </Typography>
      <CacLine projected={projected} />
    </>
  );
}

function ProjectedLtvCell({
  entry,
}: {
  entry: CreativeProductEconomicsEntry;
}): React.ReactElement {
  const projected = entry.projected;
  const currency = entry.originalCurrency;
  const exponent = entry.currencyExponent;
  if (!projected || projected.state === 'unavailable' || !currency || exponent === undefined) {
    return <Typography variant="caption" color="text.secondary">n/a</Typography>;
  }
  const money = (minor: number): string => formatMinorMoney(minor, currency, exponent);
  if ((projected.modelClass === 'sbg_monthly' || projected.modelClass === 'sbg_weekly')
    && projected.ltv12PerEntrantP50Minor !== undefined) {
    return (
      <>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {money(projected.ltv12PerEntrantP50Minor)}
        </Typography>
        {projected.ltv12PerEntrantP10Minor !== undefined
          && projected.ltv12PerEntrantP90Minor !== undefined && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`P10 ${money(projected.ltv12PerEntrantP10Minor)} — P90 ${money(projected.ltv12PerEntrantP90Minor)}`}
          </Typography>
        )}
        {projected.trialDecomposition && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {`decomposed: ${projected.trialDecomposition.pConvert.toFixed(2)} × ${money(projected.trialDecomposition.ltv12PerConvertedP50Minor)}`}
          </Typography>
        )}
      </>
    );
  }
  if (projected.ltv12PerEntrantP50Minor !== undefined) {
    return (
      <>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          {money(projected.ltv12PerEntrantP50Minor)}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {projected.modelClass === 'one_time'
            ? 'single payment'
            : 'upfront value only · no renewal projected'}
        </Typography>
      </>
    );
  }
  return <Typography variant="caption" color="text.secondary">n/a</Typography>;
}

/**
 * D9 C1 display: the CAC figure is campaign-grain and identical across the
 * deployment's products — the caveat renders with every line; degenerate
 * classes get labels only, never a manufactured cycle count.
 */
function CacLine({
  projected,
}: {
  projected: NonNullable<CreativeProductEconomicsEntry['projected']>;
}): React.ReactElement | null {
  const line = projected.checksToCoverCac;
  if (line.state === 'unavailable') {
    return (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {`CAC line n/a · ${humanReasons(line.reasonCodes) || 'unavailable'}`}
      </Typography>
    );
  }
  const cacLabel = line.cacMinor !== undefined && line.currency && line.currencyExponent !== undefined
    ? formatMinorMoney(line.cacMinor, line.currency, line.currencyExponent)
    : 'CAC';
  const text = line.state === 'available'
    ? `${line.checks} checks to cover campaign CAC ${cacLabel}`
    : line.state === 'covered_at_purchase'
      ? `covered at purchase (campaign CAC ${cacLabel})`
      : `not covered by first payment — no renewal projection (campaign CAC ${cacLabel})`;
  return (
    <Tooltip title="Campaign-grain CAC — it does not decompose per product; this is a value-side comparison, never a decision.">
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {text}
      </Typography>
    </Tooltip>
  );
}

/**
 * D9 realized side: the payback date/state is never rendered without its
 * coverage chip — the pairing lives in this one component by construction.
 */
function RealizedPaybackLine({
  economics,
}: {
  economics: CreativeProductEconomics;
}): React.ReactElement {
  const payback = economics.realizedPayback;
  const coverage = payback.spendCoverage;
  const coverageChip = (
    <Chip
      size="small"
      variant="outlined"
      color={coverage.state === 'full' ? 'success' : 'warning'}
      label={coverage.state === 'full'
        ? `spend coverage full (${coverage.completeDays}/${coverage.windowDays}d)`
        : coverage.state === 'partial'
          ? `spend coverage partial (${coverage.completeDays}/${coverage.windowDays}d · ${humanReasons(coverage.reasonCodes)})`
          : `spend coverage unavailable${coverage.reasonCodes.length > 0 ? ` (${humanReasons(coverage.reasonCodes)})` : ''}`}
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsPaybackCoverage}
    />
  );
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      useFlexGap
      flexWrap="wrap"
      data-testid={CREATIVE_DECISIONS_TEST_IDS.drawerProductEconomicsPayback}
    >
      <Typography variant="caption" sx={{ fontWeight: 700 }}>
        Realized payback (deployment · gross audit basis):
      </Typography>
      {payback.state === 'covered' && payback.coveredAt && (
        <Typography variant="caption">{`covered ${payback.coveredAt}`}</Typography>
      )}
      {payback.state === 'not_yet' && (
        <Typography variant="caption">
          {payback.remainingMinor !== undefined && payback.currency
            && payback.currencyExponent !== undefined
            ? `not yet · ${formatMinorMoney(payback.remainingMinor, payback.currency, payback.currencyExponent)} to go`
            : 'not yet'}
        </Typography>
      )}
      {payback.state === 'unavailable' && (
        <Typography variant="caption" color="text.secondary">
          {`unavailable · ${humanReasons(payback.reasonCodes) || 'no data'}`}
        </Typography>
      )}
      {coverageChip}
    </Stack>
  );
}

interface CurrencyGroup {
  key: string;
  label: string;
  entries: CreativeProductEconomicsEntry[];
}

function groupByCurrency(entries: CreativeProductEconomicsEntry[]): CurrencyGroup[] {
  const groups = new Map<string, CreativeProductEconomicsEntry[]>();
  for (const entry of entries) {
    const key = entry.originalCurrency ?? 'unresolved';
    const bucket = groups.get(key) ?? [];
    bucket.push(entry);
    groups.set(key, bucket);
  }
  // Currencies are never summed or ranked against each other — groups render
  // in stable alphabetical order, each with its own entrant denominator.
  return [...groups.entries()]
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, groupEntries]) => {
      const entrants = groupEntries.reduce((sum, entry) => sum + entry.entrySubscriptions, 0);
      return {
        key,
        label: key === 'unresolved'
          ? `Currency unresolved · ${entrants} entrants`
          : `${key} · ${entrants} entrants`,
        entries: groupEntries,
      };
    });
}

function productKey(entry: CreativeProductEconomicsEntry): string {
  return `${entry.productId ?? ''}:${entry.priceId ?? ''}:${entry.originalCurrency ?? ''}`;
}

function productDisplayName(entry: CreativeProductEconomicsEntry): string {
  if (entry.identity.productName) return entry.identity.productName;
  return entry.productId ?? entry.priceId ?? 'Unattributed product';
}

function productNominalLabel(entry: CreativeProductEconomicsEntry): string {
  const identity = entry.identity;
  if (
    identity.priceState === 'provider'
    && identity.unitAmountMinor !== undefined
    && identity.currency
    && identity.currencyExponent !== undefined
  ) {
    const nominal = formatMinorMoney(
      identity.unitAmountMinor, identity.currency, identity.currencyExponent,
    );
    const interval = identity.interval
      ? ` / ${identity.intervalCount && identity.intervalCount > 1 ? `${identity.intervalCount} ` : ''}${identity.interval}`
      : '';
    return `${nominal}${interval}`;
  }
  return 'price identity unavailable';
}

function humanReasons(codes: string[]): string {
  return codes
    .map((code) => humanizeCreativeReasonCode(code) ?? code)
    .join(', ');
}
